import Vue from 'vue'
import VueRouter, { NavigationGuardNext, Route, RouteConfig } from 'vue-router'
import Home from '@/views/Home.vue'
import Changelog from '@/views/Changelog/Changelog.vue'
import LandingPage from '@/views/Helpdesk/LandingPage.vue'
import CreateTicket from '@/components/Tickets/Create.vue'
import EditTicket from '@/components/Tickets/Edit.vue'
import { ApolloQueryResult } from 'apollo-client'
import { MyRolesQuery, UserRole } from '@/types/graphql'
import { createProvider } from '@/vue-apollo'

Vue.use(VueRouter)

const onlyAdminsCanEnter = async(to: Route, from: Route, next: NavigationGuardNext<Vue>) => {
  try {
    const result: ApolloQueryResult<MyRolesQuery> = await createProvider()
      .defaultClient
      .query({ query: MyRolesQuery })

    if (result.data.me.roles.find((role) => role?.name === UserRole.Admin)) {
      return next()
    }

    next({ path: '/' })
  } catch (error) {
    next({ path: '/' })
  }
}

const routes: RouteConfig[] = [
  {
    path: '/',
    name: 'home',
    component: Home
  },
  {
    path: '/changelog',
    name: 'changelog',
    component: Changelog
  },
  {
    path: '/about',
    name: 'about',
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    component: () =>
      import(/* webpackChunkName: "about" */ '../views/About.vue')
  },
  {
    path: '/tracking/:packageHash',
    name: 'tracking',
    component: () => import('../views/Tracking/Tracking.vue'),
    props: true
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/Login/Login.vue')
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('../components/Login/ForgotPassword.vue')
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('../components/Login/ResetPassword.vue')
  },
  {
    path: '/activity',
    name: 'activity',
    component: () => import('../views/Activity/Activity.vue')
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/Dashboard/Dashboard.vue')
  },
  {
    path: '/files',
    name: 'files',
    component: () => import('../views/Files/Files.vue')
  },
  {
    path: '/addresses',
    name: 'addresses',
    component: () => import('../views/Addresses/Addresses.vue')
  },
  {
    path: '/address-correction/:packageId',
    name: 'addressCorrection',
    component: () => import('@/components/Package/View.vue'),
    props: true
  },
  {
    path: '/tickets',
    component: () => import('../views/Helpdesk/Index.vue'),
    children: [
      {
        path: '/',
        name: 'tickets',
        component: LandingPage
      },
      {
        path: 'create',
        name: 'tickets.create',
        component: CreateTicket
      },
      {
        name: 'ticketCreateWithPackage',
        path: 'create/:packageNumber',
        component: CreateTicket,
        props: true
      },
      {
        path: ':id',
        name: 'tickets.edit',
        component: EditTicket
      }
    ]
  },
  {
    path: '/packages',
    name: 'packages',
    component: () => import('../views/Packages/Index.vue'),
    props: true
  },
  {
    path: '/package/:packageId',
    name: 'packageView',
    component: () => import('@/components/Package/View.vue'),
    props: true
  },
  {
    path: '/pnd-packages',
    name: 'pnd-packages',
    component: () => import('@/views/PNDPackages/PNDPackages.vue'),
    props: true
  },
  {
    path: '/pnd-package/:packageId',
    name: 'pnd-package-view',
    component: () => import('@/components/Package/PNDPackageView.vue'),
    props: true
  },
  {
    path: '/stocks',
    name: 'stocks',
    component: () => import('../views/Stocks/Index.vue')
  },
  {
    path: '/recycle',
    name: 'recycle',
    component: () => import('../views/Recycle/Index.vue')
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/Profile/Index.vue')
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/Administration/Administration.vue')
  },
  {
    path: '/admin/support-ticket-tags',
    name: 'support.ticket.tags',
    component: () => import('../views/Administration/SupportTicketTags.vue'),
    beforeEnter: onlyAdminsCanEnter
  },
  // ?: The following routes are commented out because they are not requred anymore.
  // ?: All the used components are still working and in the project.
  // !: Uncomment to enable them again.
  //   {
  //     path: '/admin/iso-countries',
  //     name: 'iso.countries',
  //     component: () => import('../views/Administration/IsoCountries.vue'),
  //     beforeEnter: onlyAdminsCanEnter
  //   },
  {
    path: '/admin/representants',
    name: 'representants',
    component: () => import('../views/Administration/Representants.vue'),
    beforeEnter: onlyAdminsCanEnter
  },
  {
    path: '/admin/users',
    name: 'users',
    component: () => import('../views/Administration/Users.vue'),
    beforeEnter: onlyAdminsCanEnter
  },
  {
    path: '/admin/document-templates',
    name: 'document-templates',
    component: () => import('../views/Administration/DocumentTemplates/DocumentTemplates.vue')
  },
  {
    path: '/500',
    name: '500',
    component: () => import('../views/ErrorPage/500.vue')
    // TODO!! ADD BYPASS AUTH MIDDLEWARE
    // meta: {
    //   requiresAuth: false
    // }
  },
  {
    path: '/:catchAll(.*)',
    name: 'not.found',
    component: () => import('../views/ErrorPage/404.vue')
    // TODO!! ADD BYPASS AUTH MIDDLEWARE
    // meta: {
    //   requiresAuth: false
    // }
  }

]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

export default router
